import { defaultCustomizations } from '@/store/slices/scheduled-emails/defaults';
import type { Campaign } from '@/types/campaign.types';
import type {
  IInsertScheduledEmailRequest,
  IScheduledEmail,
  IScheduledEmailContentV2,
  IScheduledEmailCustomizations,
  IScheduledEmailCustomizationsV2,
} from '@/types/email.types';
import { EmailTypeEnum } from '@/types/email.types';
import type { LanguagesAbbr } from '@/types/translation.types';
import { buildTranslateString, loadEmailTranslations } from './email-translations';

// Keys used to persist Campaign-specific fields inside the customizations index signature,
// since IScheduledEmail has no dedicated fields for these editor values.
export const CAMPAIGN_TITLE_KEY = 'campaign_title';
export const CAMPAIGN_TEMPLATE_KEY = 'campaign_template_id';
export const CAMPAIGN_DESCRIPTION_KEY = 'description';
export const CAMPAIGN_HEADING_FONT_KEY = 'headingFont';
export const CAMPAIGN_BODY_FONT_KEY = 'bodyFont';

export interface EmailEditorFields {
  title: string;
  description: string;
  salutation: string;
  startIntro: string;
  cta: string;
  subject: string;
  headingFont: string;
  bodyFont: string;
  mainImage: string;
  headingImage: string;
  secondaryImage: string;
  backgroundColor: string;
  bodyTextColor: string;
  containerColor: string;
  accentColor: string;
  headingTextColor: string;
  secondaryBackgroundColor: string;
  secondaryAccentColor: string;
  buttonTextColor: string;
}

/**
 * Reads the editable campaign fields from a scheduled email for a given language.
 * Prefers working_customizations (the in-progress draft) over published customizations.
 */
export function getEmailEditorFields(email: IScheduledEmail): IScheduledEmailContentV2 {
  return email.customizations.v2 as IScheduledEmailContentV2;
}

export interface ScheduledEmailContext {
  event_uuid: string;
  channel: number;
  email_type: EmailTypeEnum;
  languages: string[];
  baseLanguage: string;
  customizations?: IScheduledEmailCustomizations;
  timezone?: string;
}

function deriveCampaignState(email: IScheduledEmail): Campaign['state'] {
  if (email.sent) return 'sent';
  if (email.draft) return 'draft';
  if (email.delivery_date && email.delivery_date > Date.now()) return 'scheduled';
  return 'published';
}

/**
 * Converts a persisted IScheduledEmail back into the Campaign shape the UI expects.
 * Title and template_id are recovered from the _campaign_* keys stored in customizations.
 */
export function scheduledEmailToCampaign(email: IScheduledEmail): Campaign {
  const content = email.customizations.v2 as IScheduledEmailContentV2 | undefined;
  const title = content?.campaignTitle || content?.subject.base || email.email_type;
  const templateId = content?.templateId ?? 'capri';

  return {
    uuid: email.uuid,
    title,
    template_id: templateId,
    state: deriveCampaignState(email),
    stats: { sent: 0, openRate: 0, clickRate: 0 },
    scheduledAt: email.delivery_date ? new Date(email.delivery_date).toISOString() : undefined,
    thumbnailUrl: content?.mainImage as string,
    sourceScheduledEmail: email,
  };
}

/**
 * Converts a Campaign + the required scheduling context into an IInsertScheduledEmailRequest
 * suitable for posting to the API. The campaign title and template_id are stored in the
 * customizations so they survive the round-trip back through scheduledEmailToCampaign.
 */
export async function campaignToScheduledEmailRequest(
  campaign: Campaign,
  context: ScheduledEmailContext,
  eventData: {
    description: string;
    salutation: string;
    startIntro: string;
    cta: string;
    mainImage?: string;
    headingImage: string;
    headingFont?: string;
    bodyFont?: string;
    secondaryImage?: string;
    backgroundColor?: string;
    bodyTextColor?: string;
    containerColor?: string;
    accentColor?: string;
    headingTextColor?: string;
    secondaryBackgroundColor?: string;
    secondaryAccentColor?: string;
    buttonTextColor?: string;
    blocks?: IScheduledEmailContentV2['blocks'];
  }
): Promise<IInsertScheduledEmailRequest> {
  const { event_uuid, channel, email_type, languages, baseLanguage, timezone } = context;
  const { salutation, startIntro, cta, description, ...rest } = eventData;
  const templateId = campaign.template_id ?? 'capri';
  const allLanguages = Array.from(new Set([baseLanguage, ...languages]));
  const translations = await loadEmailTranslations(allLanguages);

  const customizations: IScheduledEmailCustomizationsV2 = { v2: { ...defaultCustomizations, ...rest } };
  customizations.v2.subject = { base: campaign.title, changed: '' };
  // salutation/cta are template static defaults, so they use the same `<templateId>.<field>` keys
  customizations.v2.salutation = buildTranslateString({
    key: `${templateId}.salutation`,
    fallback: salutation,
    languages: allLanguages,
    baseLanguage,
    translations,
  });
  customizations.v2.cta = buildTranslateString({
    key: `${templateId}.cta`,
    fallback: cta,
    languages: allLanguages,
    baseLanguage,
    translations,
  });
  customizations.v2.startIntro = { base: startIntro, changed: '' };
  // description is the event's own copy rather than template copy, so it isn't translated here
  customizations.v2.description = { base: description, changed: '' };
  customizations.v2.templateId = templateId;
  customizations.v2.campaignTitle = campaign.title;

  for (const language of allLanguages) {
    customizations.v2.startIntro[language as LanguagesAbbr] = translations.get(language)?.eventStart ?? startIntro;
  }

  return {
    event_uuid,
    channel,
    email_type,
    sent: false,
    delivery_date: campaign.scheduledAt ? new Date(campaign.scheduledAt).getTime() : 0,
    customizations,
    language: baseLanguage as LanguagesAbbr,
    timezone,
  };
}

export async function campaignToRegistrationEmail(
  campaign: Campaign,
  context: ScheduledEmailContext,
  eventData: {
    description: string;
    mainImage?: string;
    headingImage: string;
    headingFont?: string;
    bodyFont?: string;
    secondaryImage?: string;
    backgroundColor?: string;
    bodyTextColor?: string;
    containerColor?: string;
    accentColor?: string;
    headingTextColor?: string;
    secondaryBackgroundColor?: string;
    secondaryAccentColor?: string;
    buttonTextColor?: string;
    blocks?: IScheduledEmailContentV2['blocks'];
  }
): Promise<IInsertScheduledEmailRequest> {
  const { event_uuid, channel, email_type, languages, baseLanguage, timezone } = context;
  const { description, ...rest } = eventData;
  const templateId = campaign.template_id ?? 'capri';
  const allLanguages = Array.from(new Set([baseLanguage, ...languages]));
  const translations = await loadEmailTranslations(allLanguages);

  const customizations: IScheduledEmailCustomizationsV2 = { v2: { ...defaultCustomizations, ...rest } };
  customizations.v2.subject = { base: campaign.title, changed: '' };
  customizations.v2.salutation = { base: 'Registration for', changed: '' };
  customizations.v2.startIntro = { base: 'Click here to join:', changed: '' };
  customizations.v2.cta = { base: 'Watch session', changed: '' };
  customizations.v2.description = { base: description, changed: '' };
  customizations.v2.templateId = templateId;
  customizations.v2.campaignTitle = campaign.title;

  for (const language of allLanguages) {
    const bundle = translations.get(language);
    customizations.v2.salutation[language as LanguagesAbbr] =
      bundle?.['subject.event_registration_email'] ?? 'Registration for';
    customizations.v2.startIntro[language as LanguagesAbbr] = bundle?.eventStart ?? 'The show starts on';
    customizations.v2.cta[language as LanguagesAbbr] = bundle?.WatchSession ?? 'Watch session';
  }

  return {
    event_uuid,
    channel,
    email_type,
    sent: false,
    delivery_date: campaign.scheduledAt ? new Date(campaign.scheduledAt).getTime() : 0,
    customizations,
    language: baseLanguage as LanguagesAbbr,
    timezone,
  };
}
